import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

export default function CommiditiesTable({ type, values, commoditiesRef }) {
  // Flatten all commodities from all stops into a single array with stop info
  const allCommodities =
    values?.orderStops?.flatMap((stop, stopIndex) => {
      const stopLevel = Array.isArray(stop?.commodities)
        ? stop.commodities
        : [];
      const legLevel = Array.isArray(stop?.legs)
        ? stop.legs.flatMap((leg) =>
            Array.isArray(leg?.commodities) ? leg.commodities : []
          )
        : [];

      const resolvedCommodities = stopLevel.length > 0 ? stopLevel : legLevel;

      return resolvedCommodities.map((commodity) => ({
        ...commodity,
        stopType: stop.stopType || commodity?.type?.name || "",
        stopName:
          stop.consignee?.name || stop.shipper?.name || `Stop ${stopIndex + 1}`,
      }));
    }) || [];

  // Calculate total weight across all commodities
  const totalWeight = allCommodities.reduce(
    (sum, commodity) => {
      const weightValue = parseFloat(commodity?.weight || 0);
      return sum + weightValue;
    },
    0
  );
  const totalDimWeightFromAPI = allCommodities.reduce(
    (sum, commodity) => sum + parseFloat(commodity?.dim || 0),
    0
  );

  // Function to calculate total DIM by taking max of dim and weight for each commodity
  const calculateTotalDim = (commodities) => {
    return commodities.reduce((sum, commodity) => {
      const dimValue = parseFloat(commodity?.dim || 0);
      const weightValue = parseFloat(commodity?.weight || 0);
      const maxValue = Math.max(dimValue, weightValue);

      return sum + maxValue;
    }, 0);
  };

  const totalDimWeight = Math.round(
    allCommodities.reduce((sum, c) => {
      const qty = Number.parseFloat(c.quantity) || 0;
      const lg =
        Number.parseFloat(c.lengthValue > 72 ? 96 : c.lengthValue) || 0;
      const wd = Number.parseFloat(c.width > 72 ? 96 : c.width) || 0;
      const ht = Number.parseFloat(c.height > 72 ? 96 : c.height) || 0;
      return sum + (qty * lg * wd * ht) / 172.8;
    }, 0)
  );

  // Calculate the new total DIM using the max of dim and weight for each commodity
  const totalDimMax = calculateTotalDim(allCommodities);
  return (
    <Box sx={{ my: 4, px: 2, py: 2 }} ref={commoditiesRef}>
      <Typography variant="h6" component="h2" gutterBottom>
        Commodities ({allCommodities.length || 0})
      </Typography>

      <Paper sx={{ mt: 2 }}>
        <Table
          size="small"
          sx={{
            border: "1.5px solid #e0e0e0",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <TableHead sx={{ bgcolor: "#f8f9fd" }}>
            <TableRow>
              <TableCell>Stop</TableCell>
              <TableCell>Description</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>QTY</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>LG</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>WD</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>HT</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>PCS</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>LF</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Total Wt</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Class</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              borderRadius: 0,
              border: "1.5px solid #e0e0e0",
              overflow: "hidden",
            }}
          >
            {allCommodities.length > 0 ? (
              allCommodities.map((commodity, index) => (
                <TableRow key={index} sx={{ bgcolor: "white" }}>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    <Box>
                      <div style={{ fontWeight: "bold" }}>
                        {commodity.stopType === "PICKUP"
                          ? "Pickup"
                          : "Delivery"}
                      </div>
                      <div style={{ fontSize: "0.8em", color: "#666" }}>
                        {commodity.stopName}
                      </div>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.descriptions || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.quantity || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.typeName || commodity?.type?.name || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.lengthValue || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.width || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.height || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.pieces || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.lf || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.weight || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", py: 1 }}>
                    {commodity?.class || "-"}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: "center", py: 2 }}>
                  No commodities found
                </TableCell>
              </TableRow>
            )}

            {allCommodities.length > 0 && (
              <>
                <TableRow sx={{ bgcolor: "#f8f9fd" }}>
                  <TableCell
                    colSpan={9}
                    sx={{ textAlign: "right", fontWeight: "bold" }}
                  >
                    Total Weight:
                  </TableCell>
                  <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                    {totalWeight.toFixed(2)} lbs
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: "#f8f9fd" }}>
                  <TableCell
                    colSpan={9}
                    sx={{ textAlign: "right", fontWeight: "bold" }}
                  >
                    Total DIM:
                  </TableCell>
                  <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                    {totalDimWeightFromAPI.toFixed(2) > 0
                      ? totalDimWeightFromAPI.toFixed(2)
                      : totalDimWeight.toFixed(2) || "-"}{" "}
                    lbs
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: "#f8f9fd" }}>
                  <TableCell
                    colSpan={9}
                    sx={{ textAlign: "right", fontWeight: "bold" }}
                  >
                    Total DIM (Max of Dim/Weight):
                  </TableCell>
                  <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                    {totalDimMax.toFixed(2)} lbs
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Paper>
      <Typography
        sx={{
          textAlign: "right",
          fontStyle: "italic",
          fontWeight: "bold",
          color: "error.main",
          fontSize: 12,
          marginLeft: "auto",
        }}
      >
        * Note: DIM weight may change based on tariff cubic density.{" "}
      </Typography>
    </Box>
  );
}
